import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuthToken } from "@/lib/auth";
import { normalizeJobStatus } from "@/lib/jobStatus";
import type { LiveJob } from "@/lib/buildLabels";
import { isUserBusy, shareMap } from "@/lib/quietRefresh";

/** The admin job rows of `ids` (status, peak RSS, cache verdict, failure
 *  class), polled every 5 s while any is open or `live`.
 *
 *  Polling is unobtrusive: a poll is skipped (and retried on the next beat)
 *  while the tab is hidden or the user is selecting text, typing or
 *  scrolling, and an answer that changed nothing keeps the previous Map, so
 *  the open step list does not re-render at all. */
export function useLiveJobs(
  ids: readonly string[],
  live: boolean,
): ReadonlyMap<string, LiveJob> {
  const token = useAuthToken();
  const [jobs, setJobs] = useState<ReadonlyMap<string, LiveJob>>(new Map());
  const key = [...new Set(ids)].sort().join(",");
  useEffect(() => {
    if (!key) return;
    const n = key.split(",").length;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let first = true;
    const load = () => {
      if (!first && isUserBusy()) {
        timer = setTimeout(load, 2_000);
        return;
      }
      first = false;
      api<{ ok: true; jobs: LiveJob[] }>(
        token,
        `/admin/jobs?ids=${encodeURIComponent(key)}&limit=${Math.max(n, 1)}`,
      )
        .then((r) => {
          if (cancelled) return;
          const next = new Map(
            r.jobs.map((j) => [j.id, normalizeJobStatus(j)] as const),
          );
          setJobs((prev) => shareMap(prev, next));
          const open = r.jobs.some(
            (j) => j.status === "queued" || j.status === "running",
          );
          if (live || open) timer = setTimeout(load, 5_000);
        })
        .catch(() => {
          if (!cancelled && live) timer = setTimeout(load, 10_000);
        });
    };
    load();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [token, key, live]);
  return jobs;
}
